// SocialPostExporter — mounts a Social Post design (JSON template or
// legacy hand-coded component) in a hidden 1080×1080 node and exposes
// an imperative `exportAsPng(exportWidth)` ref method. Any surface
// that needs to render a Social Post as a final PNG can use this:
// StepSocialFinal (eventually), Recreate runtime, future scheduler /
// export tools.
//
// Why a component (not a pure function): html-to-image captures DOM,
// which means React has to render the design tree first. Wrapping the
// mount + capture path in a forwardRef component keeps callers free
// of the "mount node, wait for fonts, capture, unmount" dance.
//
// Mount pattern matches StepSocialFinal exactly: outer `position:
// absolute; width:0; height:0; overflow:hidden` so the 1080×1080
// inner div is in document flow with valid layout (html-to-image
// needs that — `position:fixed; top:-99999px` produces blank PNGs)
// but visually invisible to the user.

import {
  forwardRef,
  useImperativeHandle,
  useRef,
  type ReactNode,
} from 'react';
import { toPng } from 'html-to-image';

import type { SocialPostSlots } from '@/lib/api';
import type { SocialDesignId } from '@/pages/app/components/StepSocialDesign';
import JsonSocialPost from '@/pages/app/components/social-designs/json-templates/JsonSocialPost';
import { getJsonTemplate } from '@/pages/app/components/social-designs/json-templates/registry';
import PostEditorial from '@/pages/app/components/social-designs/PostEditorial';
import PostBlush from '@/pages/app/components/social-designs/PostBlush';
import PostBotanical from '@/pages/app/components/social-designs/PostBotanical';
import PostMono from '@/pages/app/components/social-designs/PostMono';
import PostNoir from '@/pages/app/components/social-designs/PostNoir';
import PostPop from '@/pages/app/components/social-designs/PostPop';
import PostVintage from '@/pages/app/components/social-designs/PostVintage';
import PostStreet from '@/pages/app/components/social-designs/PostStreet';
import {
  DESIGN_REFERENCE_SIZE,
  getDesignBackgroundColor,
} from '@/lib/socialPostPipeline';


// ---------------------------------------------------------------------------
// SocialPostDesign — stateless renderer that picks JSON template vs.
// legacy component based on designId. Exported as a named export so
// surfaces that want a live preview (not a hidden export) can use it
// directly without the exporter wrapper.
// ---------------------------------------------------------------------------

export interface SocialPostDesignProps {
  designId: SocialDesignId | string;
  slots: SocialPostSlots;
  /** Data URL / blob URL / signed URL — what gets rendered in the
   *  product slot. Caller chooses raw vs. composed. */
  productImageUrl: string;
  /** Scene-merge result. When set, the design renders this as the
   *  full background and skips the product slot. */
  mergedSceneUrl?: string | null;
  brandLogoUrl?: string | null;
}

export function SocialPostDesign({
  designId,
  slots,
  productImageUrl,
  mergedSceneUrl,
  brandLogoUrl,
}: SocialPostDesignProps): ReactNode {
  const designProps = { slots, productImageUrl };

  // JSON registry FIRST — these IDs are loaded from
  // social-designs/json-templates/*.json at build time.
  const jsonTpl = getJsonTemplate(designId);
  if (jsonTpl) {
    return (
      <JsonSocialPost
        config={jsonTpl}
        {...designProps}
        mergedSceneUrl={jsonTpl.composeMode === 'scene-merge' ? mergedSceneUrl ?? null : null}
        brandLogoUrl={brandLogoUrl ?? null}
      />
    );
  }

  // Legacy hand-coded fallback. Keep the switch in sync with
  // StepSocialFinal's identical switch — these are the 8 May 19
  // hand-authored designs that predate the JSON system.
  switch (designId) {
    case 'editorial': return <PostEditorial {...designProps} />;
    case 'blush':     return <PostBlush {...designProps} />;
    case 'botanical': return <PostBotanical {...designProps} />;
    case 'mono':      return <PostMono {...designProps} />;
    case 'noir':      return <PostNoir {...designProps} />;
    case 'pop':       return <PostPop {...designProps} />;
    case 'vintage':   return <PostVintage {...designProps} />;
    case 'street':    return <PostStreet {...designProps} />;
    default:
      return (
        <div style={{
          width: DESIGN_REFERENCE_SIZE,
          height: DESIGN_REFERENCE_SIZE,
          background: '#ddd',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <p>Unknown design &quot;{designId}&quot;</p>
        </div>
      );
  }
}


// ---------------------------------------------------------------------------
// SocialPostExporter — imperative ref handle for capturing the
// mounted design as a PNG. Mount this once with the design props,
// then call exportAsPng() any number of times.
// ---------------------------------------------------------------------------

export interface SocialPostExporterHandle {
  /** Capture the mounted design as a PNG data URL. `exportWidth` is
   *  the final pixel width (e.g. 1080 for 1:1 Instagram, 1200 for
   *  LinkedIn). pixelRatio is computed automatically against
   *  DESIGN_REFERENCE_SIZE (1080). Awaits document.fonts.ready before
   *  capture so headlines never wrap to fallback-font metrics. */
  exportAsPng(exportWidth: number): Promise<string>;
}

export interface SocialPostExporterProps extends SocialPostDesignProps {}

const SocialPostExporter = forwardRef<SocialPostExporterHandle, SocialPostExporterProps>(
  function SocialPostExporter(props, ref) {
    const nodeRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      async exportAsPng(exportWidth: number) {
        if (!nodeRef.current) {
          throw new Error('SocialPostExporter not mounted');
        }
        await document.fonts.ready;
        const ratio = exportWidth / DESIGN_REFERENCE_SIZE;
        return toPng(nodeRef.current, {
          pixelRatio: ratio,
          cacheBust: true,
          backgroundColor: getDesignBackgroundColor(props.designId),
        });
      },
    }), [props.designId]);

    return (
      // Outer wrapper: present in flow but clipped to 0×0. html-to-image
      // captures the inner node's actual computed styles correctly —
      // off-screen positioning (top:-99999px) produces blank PNGs.
      <div
        style={{
          position: 'absolute',
          width: 0,
          height: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
        }}
        aria-hidden="true"
      >
        <div
          ref={nodeRef}
          style={{
            width: DESIGN_REFERENCE_SIZE,
            height: DESIGN_REFERENCE_SIZE,
            position: 'relative',
          }}
        >
          <SocialPostDesign {...props} />
        </div>
      </div>
    );
  },
);

export default SocialPostExporter;
